import { Button, IconButton, Stack, StackProps } from '@mui/material';
import { DEFAULT_DATE_FORMAT_FNS, FilterKeys, GET_FILTER_QUERY } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useSchoolPeriods } from '@schooly/components/filters';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CrossIcon,
  DeleteIcon,
  Loading,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeaderInput,
  ModalMain,
  ModalSmall,
  Spin,
  StarIcon,
  Stepper,
} from '@schooly/style';
import { getControllerErrorText } from '@schooly/utils/get-controller-error-text';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import {
  MarkbookFilterStep,
  useMarkbookFilterContext,
} from '../../../../context/markbook/MarkbookFilterContext';
import { useSchool } from '../../../../hooks/useSchool';
import useSchoolYears from '../../../../hooks/useSchoolYears';
import {
  useGetAssessmentsForMarkbook,
  useMarkbookContextNavigation,
  useMarkbookSteps,
} from './helpers';
import { AdjustManuallyStep, SelectAssessmentHeader } from './steps/AdjustManuallyStep';
import { SelectAssessmentsStep } from './steps/SelectAssessmentsStep';
import { SelectStudentsStep } from './steps/SelectStudentsStep';

export const ASSESSMENTS_LIMIT = 30;

export const MarkbookFilterModal: FC = () => {
  const { schoolId = '' } = useSchool();
  const queryClient = useQueryClient();

  const {
    handleNavigateToMainPage,
    handleUnsavedMarkbookPreview,
    handleNavigateToSavedFilterPreview,
    onGoBack,
  } = useMarkbookContextNavigation();

  const { defaultValidity } = useSchoolYears();
  const datesForPeriod: [string, string] = useMemo(
    () => [
      format(new Date(), DEFAULT_DATE_FORMAT_FNS),
      format(new Date(), DEFAULT_DATE_FORMAT_FNS),
    ],
    [],
  );

  const {
    currentPeriod,
    isLoading: isLoadingPeriods,
    currentSchoolYear,
  } = useSchoolPeriods(schoolId, datesForPeriod);

  const defaultDates: [string, string] = useMemo(() => {
    if (currentPeriod) {
      return [currentPeriod.period.date_from, currentPeriod.period.date_to];
    }
    if (currentSchoolYear) {
      return [currentSchoolYear.start, currentSchoolYear.end];
    }
    return datesForPeriod;
  }, [currentPeriod, currentSchoolYear, datesForPeriod]);

  const { getConfirmation } = useConfirmationDialog();
  const {
    filterId,
    currentStep,
    navigateToStep,
    form,
    showRecurring,
    onShowRecurringChange,
    isFilterLoading,
    handleRemoveFilter,
    isRemovingFilter,
    isUpdatingFilter,
    handleSaveFilter,
    filterPreview,
  } = useMarkbookFilterContext();

  const selectedAssessmentIds = form.watch('selectedAssessmentIds');

  const {
    entries,
    params,
    setParams,
    isAssessmentsLoading,
    isGetAllAssessmentsLoading,
    isFetchingNextPage,
    fetchNextPage,
    total,
    getSelectedAssessmentIds,
  } = useGetAssessmentsForMarkbook({
    showRecurring,
    enabled: currentStep === MarkbookFilterStep.AdjustManually,
  });

  const selectedCount = selectedAssessmentIds === 'all' ? total : selectedAssessmentIds.size;

  const isOverLimit = selectedCount > ASSESSMENTS_LIMIT;

  const { formatMessage } = useIntl();
  const {
    canNavigatePrev,
    canNavigateNext,
    activeStepIndex,
    completedSteps,
    handleNext,
    handlePrev,
    handleStepClick,
    isLastStep,
    steps,
  } = useMarkbookSteps(currentStep, navigateToStep);

  const isLoading = isAssessmentsLoading || isFetchingNextPage;

  const renderStepContent = () => {
    switch (currentStep) {
      case MarkbookFilterStep.SelectStudents:
        return <SelectStudentsStep defaultDates={defaultDates} defaultValidity={defaultValidity} />;
      case MarkbookFilterStep.SelectAssessments:
        return <SelectAssessmentsStep />;
      case MarkbookFilterStep.AdjustManually:
        return (
          <AdjustManuallyStep
            entries={entries}
            isLoading={isAssessmentsLoading}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onGoBack={() => {
              navigateToStep(MarkbookFilterStep.SelectAssessments);
            }}
          >
            <SelectAssessmentHeader
              showRecurring={showRecurring}
              onShowRecurringChange={onShowRecurringChange}
              total={total}
              query={params.query ?? ''}
              onQueryChange={(value) => setParams({ ...params, query: value })}
              selectedCount={selectedCount}
              limit={ASSESSMENTS_LIMIT}
              isOverLimit={isOverLimit}
              hasAssessments={!!entries.length && !isAssessmentsLoading}
            />
          </AdjustManuallyStep>
        );
      default:
        return null;
    }
  };

  const storeAssessmentIds = useCallback(async () => {
    const assessmentIds =
      selectedAssessmentIds === 'all'
        ? await getSelectedAssessmentIds()
        : Array.from(selectedAssessmentIds);
    if (!assessmentIds.length) {
      throw new Error('No assessment ids provided');
    }
    form.setValue(FilterKeys.Assessment, assessmentIds);
  }, [form, getSelectedAssessmentIds, selectedAssessmentIds]);

  const onCloseModal = useCallback(async () => {
    if (form.formState.isDirty) {
      const isConfirmed = await getConfirmation({
        textId: 'school-edit-CloseUnsavedConfirmation',
      });
      if (!isConfirmed) return;
    }
    onGoBack();
  }, [form.formState.isDirty, getConfirmation, onGoBack]);

  const onDeleteFilter = useCallback(async () => {
    const isConfirmed = await getConfirmation({
      textId: 'assessments-markbook-deleteMarkbookConfirmation',
    });
    if (!isConfirmed) return;
    await handleRemoveFilter();
    handleNavigateToMainPage();
  }, [getConfirmation, handleRemoveFilter, handleNavigateToMainPage]);

  const handlePreview = useCallback(async () => {
    await storeAssessmentIds();
    handleUnsavedMarkbookPreview();
  }, [storeAssessmentIds, handleUnsavedMarkbookPreview]);

  const disableActions =
    !entries.length ||
    isLoading ||
    isOverLimit ||
    !selectedCount ||
    isAssessmentsLoading ||
    isGetAllAssessmentsLoading ||
    isUpdatingFilter;

  const title =
    filterPreview?.type === 'adjustment'
      ? formatMessage({ id: 'assessments-markbook-adjustMarkbookView' })
      : formatMessage({ id: 'assessments-markbook-newMarkbook' });

  const handleUpdate = useCallback(async () => {
    await storeAssessmentIds();
    const res = await handleSaveFilter();
    if (res && filterId) {
      handleNavigateToSavedFilterPreview(filterId);
      queryClient.invalidateQueries([GET_FILTER_QUERY, filterId]);
    }
  }, [
    storeAssessmentIds,
    handleSaveFilter,
    filterId,
    handleNavigateToSavedFilterPreview,
    queryClient,
  ]);

  return (
    <ModalSmall open onClose={onCloseModal}>
      {isFilterLoading || isLoadingPeriods ? (
        <Loading />
      ) : (
        <>
          <ModalHeader
            active
            title={
              filterId ? (
                <Controller
                  control={form.control}
                  name="name"
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <ModalHeaderInput
                      placeholder={formatMessage({ id: 'assessments-markbook-Name' })}
                      autoFocus={!field.value}
                      error={!!fieldState.error}
                      helperText={getControllerErrorText(
                        fieldState.error,
                        undefined,
                        formatMessage,
                      )}
                      {...field}
                    />
                  )}
                />
              ) : (
                title
              )
            }
          >
            <IconButton onClick={onCloseModal}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>
          <Stepper
            steps={steps}
            activeStep={activeStepIndex}
            onStepClick={handleStepClick}
            completedSteps={completedSteps}
            sx={{ pt: 4 }}
          />

          <ModalMain>
            <ModalContent sx={{ p: 0, px: 0, pt: 0 }} active>
              {renderStepContent()}
            </ModalContent>
          </ModalMain>
          <ModalFooter active>
            <Stack
              direction="row"
              spacing={2}
              justifyContent={canNavigatePrev ? 'space-between' : 'flex-end'}
              width="100%"
            >
              {canNavigatePrev && (
                <Button startIcon={<ArrowLeftIcon />} variant="outlined" onClick={handlePrev}>
                  {formatMessage({ id: 'action-Back' })}
                </Button>
              )}
              <Stack direction="row" gap={2}>
                {filterId && (
                  <Button
                    variant="outlined"
                    onClick={onDeleteFilter}
                    startIcon={<DeleteIcon />}
                    endIcon={isRemovingFilter ? <Spin /> : undefined}
                    disabled={isRemovingFilter}
                  >
                    {formatMessage({ id: 'action-Delete' })}
                  </Button>
                )}

                {canNavigateNext && (
                  <Button
                    endIcon={<ArrowRightIcon />}
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canNavigateNext}
                  >
                    {formatMessage({ id: 'action-Next' })}
                  </Button>
                )}
                {isLastStep && (
                  <>
                    {filterId ? (
                      <Button
                        onClick={form.handleSubmit(handleUpdate)}
                        sx={(theme) => ({
                          '.svg-icon': { color: theme.palette.warning.main },
                        })}
                        disabled={disableActions}
                        startIcon={<StarIcon />}
                        endIcon={
                          isUpdatingFilter || isGetAllAssessmentsLoading ? <Spin /> : undefined
                        }
                      >
                        {formatMessage({ id: 'assessments-markbook-saveMarkbookAndView' })}
                      </Button>
                    ) : (
                      <Button
                        onClick={handlePreview}
                        endIcon={isGetAllAssessmentsLoading ? <Spin /> : undefined}
                        disabled={disableActions}
                      >
                        {formatMessage({ id: 'assessments-markbook-ViewMarkbook' })}
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Stack>
          </ModalFooter>
        </>
      )}
    </ModalSmall>
  );
};

export const StepWrapper: FC<PropsWithChildren & StackProps> = ({ children, ...props }) => {
  return (
    <Stack pt={4.25} gap={2} {...props}>
      {children}
    </Stack>
  );
};
